import { AUTO_GRADABLE } from '../schemas/course.js';
/**
 * Everything two spellings of the same answer may differ by.
 *
 * Diacritics are NOT stripped. Lesson 1 of this course is about ą, ę, ł, ś,
 * ź and ż; accepting "sa" for "są" would quietly teach the opposite of the
 * thing being taught. Capitalisation and trailing punctuation are noise and
 * go; letters do not.
 */
export function normalise(value) {
    return value
        .normalize('NFC')
        .replace(/[*_`]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLocaleLowerCase('pl')
        .replace(/[.,!?;:…]+$/u, '')
        .trim();
}
/**
 * The answer key writes alternatives inline: `robił / robiła`, `a · b`.
 * Both separators mean "either of these", and a slash inside a single word
 * (`m/ż`) is not a separator — hence the spaces.
 */
function variants(answer) {
    return answer
        .split(/\s+\/\s+|\s+·\s+/u)
        .map(normalise)
        .filter(Boolean);
}
function itemIsCorrect(answers, submitted) {
    const given = normalise(submitted);
    if (!given)
        return false;
    const accepted = answers.flatMap(variants);
    if (accepted.includes(given))
        return true;
    /*
     * "(2) ________ / ________" asks for both forms — `emigrant / emigrantka`.
     * Typing both is right when every one of them is; hedging between a right
     * and a wrong answer still is not.
     */
    const parts = variants(submitted);
    return parts.length > 1 && parts.every((p) => accepted.includes(p));
}
/**
 * Marks a submission. `submitted[i]` lines up with `payload.items[i]`; a
 * missing entry counts as unanswered rather than wrong-with-an-error, since
 * the two are the same to the student and only one of them needs handling.
 */
export function gradeExercise(payload, submitted) {
    if (payload.type === 'unknown' || !AUTO_GRADABLE.has(payload.type)) {
        return { correct: [], score: 0, max: 0, graded: false };
    }
    const correct = payload.items.map((item, i) => item.answers.length > 0 && itemIsCorrect(item.answers, submitted[i] ?? ''));
    return {
        correct,
        score: correct.filter(Boolean).length,
        /*
         * An item with no answer in the key cannot be got wrong, so it must not
         * count towards the total either — otherwise a gap in the source caps
         * everybody below full marks for a mistake that is not theirs.
         */
        max: payload.items.filter((i) => i.answers.length > 0).length,
        graded: true,
    };
}
